import {
  GET_BOOKING_LOOKUP,
  GET_DEVICE_LIST_WITHOUT_AUTH,
  GET_PROCEDURE_LIST,
  GET_STAFFLIST,
  GET_TIME_SLOTS,
  POST_ONLINEBOOKING_INTENT,
  SAVE_APPOINTMENT,
  SAVE_ONLINEBOOKING_TRANSACTION,
  SEND_BOOKING_OTP,
  VERIFY_BOOKING_OTP,
  GET_AVAILABLE_DATES
} from 'constants/url';
import {  AddEditAppoinmentContent } from 'models';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { HmacSHA256 } from 'crypto-js';
import Base64 from 'crypto-js/enc-base64';
import useDataService from 'services';
import { conflictResponse, responseMessages } from 'utilities/onlineBooking';
import { INVALID_REQUEST } from 'constants/utilities';
import { encodedData } from 'utilities/utils';

const hashGeneration = (clientId: string, ts: string, key: string) => {
  const hmacDigest = Base64.stringify(HmacSHA256(`${clientId}${ts}`, key));
  return hmacDigest;
};

export const useGetBookingLookup = (lookupData: any, onSuccessBookingLookup?: any) => {
  const queryClient = useQueryClient();
  const clinic = useQuery(
    'booking-lookup',
    async () => {
      const result = await useDataService.getServiceWithoutToken(
        `${GET_BOOKING_LOOKUP}?ts=${encodeURIComponent(
          lookupData.timeStamp,
        )}&hmac=${encodeURIComponent(
          hashGeneration(
            lookupData.clientId,
            lookupData.timeStamp,
            lookupData.secretKey,
          ),
        )}&tenantId=${encodedData(lookupData.clientId)}`,
      );
      return result;
    },
    {
      onSuccess: onSuccessBookingLookup,
      onError: (error: any) => {
        if (error?.toString().includes(INVALID_REQUEST)) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        } else if (error?.title) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.title.toString(),
            type: 'error',
          });
        } else {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        }
      },
    },
  );
  return clinic;
};

export const useGetTimeSlots = (
  timeSlotData?: any,
  onSuccessTimeSlots?: any,
  onErrorTimeSlots?: any
) => {
  const queryClient = useQueryClient();
  const timeSlots = useQuery(
    ['timeSlots', timeSlotData.queryStringToApi],
    async () => {
      const result = await useDataService.getServiceWithoutToken(
        `${GET_TIME_SLOTS}?${timeSlotData.queryStringToApi
        }&Ts=${encodeURIComponent(
          timeSlotData.timeStamp,
        )}&Hmac=${encodeURIComponent(
          hashGeneration(
            timeSlotData.clientId,
            timeSlotData.timeStamp,
            timeSlotData.secretKey,
          ),
        )}&TenantId=${encodedData(timeSlotData.clientId)}`,
      );
      return result;
    },
    {
      enabled: timeSlotData.queryStringToApi !== '',
      onSuccess: onSuccessTimeSlots,
      onError: (error: any) => {
        onErrorTimeSlots()
        if (error?.toString().includes(INVALID_REQUEST)) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        } else if (error?.title) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.title,
            type: 'error',
          });
        } else {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        }
      },
    },
  );
  return timeSlots;
};

export const usePostSaveAppointment = (
  onPostSaveAppointmentSuccess?: any,
  saveAppointmentApiData?: any,
  onPostSaveAppointmentError?: any,
  stripeCustomerID?: any
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation<any, Error>(
    'saveAppointmentPost',
    async () => {
      let postObj = {
        ...saveAppointmentApiData.saveAppointmentData,
        tenantId: encodedData(saveAppointmentApiData.clientId),
        ts: saveAppointmentApiData.timeStamp,
        amount: saveAppointmentApiData.amount,
        hmac: hashGeneration(
          saveAppointmentApiData.clientId,
          saveAppointmentApiData.timeStamp,
          saveAppointmentApiData.secretKey,
        ),
      }
      postObj.patientDto.stripeCustomerID = stripeCustomerID
      const result = await useDataService.authenticationPostService(
        SAVE_APPOINTMENT,
        postObj,
      );
      return result;
    },
    {
      onSuccess: onPostSaveAppointmentSuccess,
      onSettled: () => {
        queryClient.invalidateQueries('saveAppointmentPost');
      },
      onError: (error: any) => {
        onPostSaveAppointmentError();
        if (
          error?.message ===
          `${AddEditAppoinmentContent.ExistingAppointmentPatient}` ||
          error?.message ===
          `${AddEditAppoinmentContent.ExistingAppointmentStaff}`
        ) {
          conflictResponse(error.message);
        } else if (error?.message && !error?.title) {
          responseMessages(error.message);
        } else if (error?.title) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.title,
            type: 'error',
          });
        } else if (error?.toString().includes(INVALID_REQUEST)) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        } else {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        }
      },
    },
  );
  return mutation;
};

export const usePostSendOtp = (
  onPostSendOtpSuccess?: any,
  sendOtpApiData?: any,
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation<any, Error>(
    'sendOtpPost',
    async () => {
      const result = await useDataService.authenticationPostService(
        `${SEND_BOOKING_OTP}`,
        {
          ...sendOtpApiData.sendOtpData,
          tenantId: encodedData(sendOtpApiData.clientId),
          ts: sendOtpApiData.timeStamp,
          hmac: hashGeneration(
            sendOtpApiData.clientId,
            sendOtpApiData.timeStamp,
            sendOtpApiData.secretKey,
          ),
        },
      );
      return result;
    },
    {
      onSuccess: onPostSendOtpSuccess,
      onSettled: () => {
        queryClient.invalidateQueries('sendOtpPost');
      },
      onError: (error: any) => {
        if (error?.toString().includes(INVALID_REQUEST)) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        } else if (error?.title) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.title.toString(),
            type: 'error',
          });
        } else {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        }
      },
    },
  );
  return mutation;
};

export const usePostVerifyOtp = (
  onPostVerifyOtpSuccess?: any,
  verifyOtpApiData?: any,
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation<any, Error>(
    'verifyOtpPost',
    async () => {
      const result = await useDataService.authenticationPostService(
        `${VERIFY_BOOKING_OTP}`,
        {
          ...verifyOtpApiData.verifyOtpData,
          tenantId: encodedData(verifyOtpApiData.clientId),
          ts: verifyOtpApiData.timeStamp,
          hmac: hashGeneration(
            verifyOtpApiData.clientId,
            verifyOtpApiData.timeStamp,
            verifyOtpApiData.secretKey,
          ),
        },
      );
      return result;
    },
    {
      onSuccess: onPostVerifyOtpSuccess,
      onSettled: () => {
        queryClient.invalidateQueries('verifyOtpPost');
      },
      onError: (error: any) => {
        if (error?.toString().includes(INVALID_REQUEST)) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        } else if (error?.title) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.title.toString(),
            type: 'error',
          });
        } else {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        }
      },
    },
  );
  return mutation;
};

export const useGetStaffList = (
  staffData: any,
  onSuccessGetStaffList?: any,
) => {
  const queryClient = useQueryClient();
  const staffList = useQuery(
    ['staff-list', staffData.procedureId, staffData.locationId],
    async () => {
      const result = await useDataService.getServiceWithoutToken(
        `${GET_STAFFLIST}/${staffData.procedureId}?ts=${encodeURIComponent(
          staffData.timeStamp,
        )}&hmac=${encodeURIComponent(
          hashGeneration(
            staffData.clientId,
            staffData.timeStamp,
            staffData.secretKey,
          ),
        )}&locationId=${staffData.locationId}&tenantId=${encodedData(
          staffData.clientId,
        )}`,
      );
      return result;
    },
    {
      enabled: !!staffData.procedureId && !!staffData.locationId,
      onSuccess: onSuccessGetStaffList,
      onError: (error: any) => {
        if (error?.toString().includes(INVALID_REQUEST)) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        } else if (error?.title) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.title.toString(),
            type: 'error',
          });
        } else {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        }
      },
    },
  );
  return staffList;
};

export const useGetProcedureList = (
  procedureData: any,
  onSuccessGetProcedureList?: any,
) => {
  const queryClient = useQueryClient();
  const procedureList = useQuery(
    ['procedure-list', procedureData.locationId],
    async () => {
      const result = await useDataService.getServiceWithoutToken(
        `${GET_PROCEDURE_LIST}/${procedureData.locationId
        }?ts=${encodeURIComponent(
          procedureData.timeStamp,
        )}&hmac=${encodeURIComponent(
          hashGeneration(
            procedureData.clientId,
            procedureData.timeStamp,
            procedureData.secretKey,
          ),
        )}&tenantId=${encodedData(procedureData.clientId)}`,
      );
      return result;
    },
    {
      enabled: !!procedureData.locationId,
      onSuccess: onSuccessGetProcedureList,
      onError: (error: any) => {
        if (error?.toString().includes(INVALID_REQUEST)) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        } else if (error?.title) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.title.toString(),
            type: 'error',
          });
        } else {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        }
      },
    },
  );
  return procedureList;
};



export const usePostOnlineBookingIntent = (
  sendOnlineBookingIntentData: any,
  onSuccessPostOnlineBookingIntent?: any,
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation<any, Error>(
    'sendOnlineBookingIntent',
    async () => {
      const result = await useDataService.authenticationPostService(
        `${POST_ONLINEBOOKING_INTENT}`,
        sendOnlineBookingIntentData,
      );
      return result;
    },
    {
      onSuccess: onSuccessPostOnlineBookingIntent,
      onSettled: () => {
        queryClient.invalidateQueries('sendOnlineBookingIntent');
      },
      onError: (error: any) => {
        if (error?.toString().includes(INVALID_REQUEST)) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        } else if (error?.title) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.title.toString(),
            type: 'error',
          });
        } else {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        }
      },
    },
  );
  return mutation;
};


export const usePostOnlineBookingTransaction = (
  saveOnlineBookingTransactionData?: any,
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation<any, Error>(
    'saveOnlineBookingTransaction',
    async () => {
      const result = await useDataService.authenticationPostService(
        SAVE_ONLINEBOOKING_TRANSACTION,
        {
          ...saveOnlineBookingTransactionData.saveOnlineBookingTransactionData,
          tenantId: encodedData(saveOnlineBookingTransactionData.clientId),
          ts: saveOnlineBookingTransactionData.timeStamp,
          hmac: hashGeneration(
            saveOnlineBookingTransactionData.clientId,
            saveOnlineBookingTransactionData.timeStamp,
            saveOnlineBookingTransactionData.secretKey,
          ),
        },
      );
      return result;
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries('saveOnlineBookingTransaction');
      },
      onError: (error: any) => {
        if (error?.message && !error?.title) {
          responseMessages(error.message);
        } else if (error?.title) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.title,
            type: 'error',
          });
        } else if (error?.toString().includes(INVALID_REQUEST)) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        } else {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        }
      },
    },
  );
  return mutation;
};


export const useGetDeviceList = (deviceListQuery: any, onSuccessGetDeviceList?: any) => {
  const queryClient = useQueryClient();
  const deviceList = useQuery(
    ['booking-device-list', deviceListQuery],
    async () => {
      const result = await useDataService.getServiceWithoutToken(
        `${GET_DEVICE_LIST_WITHOUT_AUTH}/${deviceListQuery}`,
      );
      return result;
    },
    {
      enabled: Boolean(deviceListQuery),
      onSuccess: onSuccessGetDeviceList,
      onError: (error: any) => {
        if (error?.toString().includes(INVALID_REQUEST)) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        } else if (error?.title) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.title.toString(),
            type: 'error',
          });
        } else {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        }
      },
    },
  );
  return deviceList;
};

// export const useGetAvailableDates = (
//   timeSlotData?: any,
//   onSuccessAvailableDates?: any,
//   onErrorTimeSlots?: any
// ) => {
//   const queryClient = useQueryClient();
//   const timeSlots = useQuery(
//     ['availableDates', timeSlotData.staffId],
//     async () => {
//       const result = await useDataService.getServiceWithoutToken(
//         `${GET_AVAILABLE_DATES}?${timeSlotData.queryStringToApi
//         }&StaffId=${timeSlotData.StaffId
//         }&LocationId=${timeSlotData.LocationId
//         }&ProcedureId=${timeSlotData.ProcedureId
//         }&AppointmentDate=${timeSlotData.AppointmentDate
//         }&Ts=${encodeURIComponent(
//           timeSlotData.timeStamp,
//         )}&Hmac=${encodeURIComponent(
//           hashGeneration(
//             timeSlotData.clientId,
//             timeSlotData.timeStamp,
//             timeSlotData.secretKey,
//           ),
//         )}&TenantId=${encodedData(timeSlotData.clientId)}`,
//       );
//       return result;
//     },
//     {
//       enabled: !!timeSlotData.StaffId,
//       onSuccess: (data: any) => {
//         onSuccessAvailableDates(data)
//       },
//       onError: (error: any) => {
//         onErrorTimeSlots()
//         if (error?.toString().includes(INVALID_REQUEST)) {
//           queryClient.setQueryData(['toast-message'], {
//             message: error?.toString(),
//             type: 'error',
//           });
//         } else if (error?.title) {
//           queryClient.setQueryData(['toast-message'], {
//             message: error?.title,
//             type: 'error',
//           });
//         } else {
//           queryClient.setQueryData(['toast-message'], {
//             message: error?.toString(),
//             type: 'error',
//           });
//         }
//       },
//     },
//   );
//   return timeSlots;
// };


export const useGetAvailableDates = (timeSlotData?: any, onSuccessAvailableDates?: any) => {
  const queryClient = useQueryClient();
  const { refetch: refetchStaffLocation,
    isLoading: isMonthDataLoading,
    isRefetching: isMonthDataRefetching,
    remove: stopFetchingMonthData
  } = useQuery(
    'availableDates',
    async () => {
      const result = await useDataService.getServiceWithoutToken(
        `${GET_AVAILABLE_DATES}?StaffId=${timeSlotData.StaffId
        }&LocationId=${timeSlotData.LocationId
        }&ProcedureId=${timeSlotData.ProcedureId
        }&AppointmentDate=${timeSlotData.AppointmentDate
        }&Ts=${encodeURIComponent(
          timeSlotData.timeStamp,
        )}&Hmac=${encodeURIComponent(
          hashGeneration(
            timeSlotData.clientId,
            timeSlotData.timeStamp,
            timeSlotData.secretKey,
          ),
        )}&TenantId=${encodedData(timeSlotData.clientId)}`,
      );
      return result;
    },
    {
      enabled: !!timeSlotData.StaffId,
      onSuccess: onSuccessAvailableDates,
      onError: (error: any) => {
        if (error?.toString().includes(INVALID_REQUEST)) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        } else if (error?.title) {
          queryClient.setQueryData(['toast-message'], {
            message: error?.title.toString(),
            type: 'error',
          });
        } else {
          queryClient.setQueryData(['toast-message'], {
            message: error?.toString(),
            type: 'error',
          });
        }
      },
    },
  );
  return { refetchStaffLocation, isMonthDataLoading, isMonthDataRefetching, stopFetchingMonthData };
}